import React, { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Chip from '@material-ui/core/Chip'
import { useMediaQuery } from '@material-ui/core'
import textVersion from 'textversionjs'
import DOMPurify from 'dompurify'

import { AddTeamModal } from '.'
import styles from '../../styles'
import localStyles from './styles'
import { buildTeamCover, combineStyles, PlaceholderContent } from 'utils'
import CompanyHeaderImage from 'assets/images/company-header-image.jpg'
import {
	EEntityAboutSectionType,
	IPublicCompany,
	IPublicCompanyTeam,
} from 'models'
import { EntityAboutSection, Loading } from 'components'
import classNames from 'classnames'
import commonStyles from '../../../../styles/common'
import { useCompanyQuery } from 'graphql/__generated__/graphql'
import { useApolloClient } from '@apollo/react-hooks'
import { PublicCompanyQuery, PublicCompanyTeamsQuery } from 'store'

const CompanyTeams: React.FunctionComponent<any> = props => {
	const { canEdit, companyTeamsDescription } = props
	const [publicCompany, setPublicCompany] = useState<IPublicCompany>()
	const [teamsData, setTeamsData] = useState<IPublicCompanyTeam[]>()
	const client = useApolloClient()
	const { companySlug }: { companySlug?: string } = useParams()
	const combinedStyles: any = combineStyles(commonStyles, styles, localStyles)
	const useStyles = makeStyles(combinedStyles)
	const classes = useStyles()
	const { t } = useTranslation()
	const [openAddTeamModal, setOpenAddTeamModal] = useState(false)
	const isMobile = useMediaQuery('@media (max-width:650px)')

	const toggleAddTeamModal = useCallback(() => {
		setOpenAddTeamModal(state => !state)
	}, [])

	const [, setTeamResourceContainerHeight] = useState(208)
	const { data: companyData } = useCompanyQuery({
		variables: {
			slug: '%' + companySlug + '%',
			// language: i18n.language,
		},
	})

	const company = useMemo(
		() =>
			(companyData &&
				companyData?.companyCollection?.edges?.map(x => x.node))?.[0] ||
			null,
		[companyData],
	)

	// const { data: teamsData, loading: teamsLoading } = useTeamQueryQuery({
	// 	fetchPolicy: 'network-only',
	// 	variables: {
	// 		companyId,
	// 	},
	// })

	// const handleGoToTeamProfile = useCallback(
	// 	(teamSlug: string) => () => {
	// 		history.push(
	// 			`/${i18n.language}/employers/${companySlug}/teams/${teamSlug}`,
	// 		)
	// 	},
	// 	[history, i18n.language, companySlug],
	// )

	useEffect(() => {
		const handleWindowResize = () =>
			setTeamResourceContainerHeight((260 * window.innerWidth) / 650)
		window.addEventListener('resize', handleWindowResize)

		return () => {
			window.removeEventListener('resize', handleWindowResize)
		}
	}, [])

	useEffect(() => {
		if (company?.name)
			client
				.query({
					query: PublicCompanyQuery,
					variables: {
						name: company?.name,
					},
				})
				.then((compnayResponse: any) => {
					client
						.query({
							query: PublicCompanyTeamsQuery,
							variables: {
								companyId:
									compnayResponse.data
										.public_companiesCollection.edges[0]
										.node?.id,
							},
						})
						.then((teamsResponse: any) => {
							setTeamsData(
								teamsResponse.data.company_public_teamsCollection.edges.map(
									(x: any) => x.node,
								) || [],
							)
							setPublicCompany(
								compnayResponse.data.public_companiesCollection
									.edges[0].node,
							)
						})
				})
	}, [client, company])

	return (
		<Grid
			className={classes.companyMainContent}
			container={true}
			direction="column"
		>
			{canEdit && (
				<Chip
					onClick={toggleAddTeamModal}
					style={{
						margin: 2,
						background: '#fff',
					}}
					label={'Add team'}
					variant="outlined"
					color={'secondary'}
					clickable={true}
					classes={{
						label: classes.editIconLabel,
						root: classes.addTeamElement,
					}}
				/>
			)}

			<AddTeamModal
				companyId={company?.id || null}
				openAddTeamModal={openAddTeamModal}
				toggleAddTeamModal={toggleAddTeamModal}
			/>

			<EntityAboutSection
				type={EEntityAboutSectionType.companyTeams}
				title={t('Meet our-1')}
				emphasizedTitle={t('Teams-1')}
				oneRowTitle={false}
				content={companyTeamsDescription}
				canEdit={canEdit}
				companyId={company?.id}
			/>
			{teamsData === undefined ? (
				<Loading size={48} style={{ margin: '100px auto' }} />
			) : (
				<Grid container={true} direction="column">
					{teamsData && teamsData.length > 0 ? (
						teamsData?.map((item, index: number) => {
							const plainTextDescription = textVersion(
								DOMPurify.sanitize(item.description ?? ''),
							)
							return (
								<Grid
									container={true}
									className={classNames(
										classes.teamItem,
										classes.defaultShadow,
									)}
									style={{
										flexDirection: 'column',
									}}
									key={`team ${index}`}
								>
									<Grid
										item={true}
										xs={isMobile ? 12 : 8}
										md={8}
										className={
											classes.teamResourceContainer
										}
										style={{
											minHeight: 'undefined',
											maxWidth: '100%',
										}}
										// style={{
										// 	minHeight:
										// 		isMobile && item.featuredVideo
										// 			? `${teamResourceContainerHeight}px`
										// 			: 'undefined',
										// }}
									>
										{item.image_path && (
											<img
												src={buildTeamCover({
													coverPath: item.image_path,
													id: item.id,
												})}
												alt={item.title}
												// onClick={handleGoToTeamProfile(
												// 	item.slug,
												// )}
											/>
										)}
										{/* {item.hasCover && (
											<img
												src={buildTeamCover(item)}
												alt={item.name}
												onClick={handleGoToTeamProfile(
													item.slug,
												)}
											/>
										)} */}
										{/* {!item.hasCover && item.featuredVideo && (
											<div className={classes.video}>
												<ReactPlayer
													url={item.featuredVideo}
													className={classes.player}
													controls={true}
													width="100%"
													height="100%"
													style={{
														flex: 1,
														overflow: 'hidden',
													}}
												/>
											</div>
										)} */}
										{/* {!item.hasCover &&
											!item.featuredVideo && (
												<img
													alt={item.name}
													src={CompanyHeaderImage}
													onClick={handleGoToTeamProfile(
														item.slug,
													)}
												/>
											)} */}
										{!item.image_path && (
											<img
												alt={item.title}
												src={CompanyHeaderImage}
												// onClick={handleGoToTeamProfile(
												// 	item.slug,
												// )}
											/>
										)}
									</Grid>
									<Grid
										item={true}
										xs={isMobile ? 12 : 4}
										md={4}
										className={classes.teamItemInformation}
										// onClick={handleGoToTeamProfile(
										// 	item.slug,
										// )}
										style={{ maxWidth: '100%' }}
									>
										<Grid
											container={true}
											direction="column"
										>
											<h3
												className={
													classes.teamItemTitle
												}
												style={{ marginTop: 0 }}
											>
												{item.title}
											</h3>

											{plainTextDescription}
											{/* {plainTextDescription.length >
											200 ? (
												<div
													className={
														classes.teamItemDescription
													}
												>
													{`${plainTextDescription.substr(
														0,
														200,
													)}`}
													&nbsp;
													<span
														className={
															classes.seeMore
														}
													>
														{`...`}
													</span>
												</div>
											) : (
												plainTextDescription
											)} */}
										</Grid>
										{/* <Fab
											variant="extended"
											size="small"
											color="primary"
											aria-label="small"
											style={{
												position: 'absolute',
												bottom: '10px',
												right: '10px',
												textTransform: 'none',
												fontSize: '12px',
												height: '25px',
											}}
											onClick={handleGoToTeamProfile(
												item.slug,
											)}
										>
											{t('Learn more') || ''}
										</Fab> */}
									</Grid>
								</Grid>
							)
						})
					) : (
						<PlaceholderContent
							data={publicCompany?.name || t('the company')}
							entity={'teams'}
						/>
					)}
				</Grid>
			)}
		</Grid>
	)
}

export default memo(CompanyTeams)
